<template>
	<nav
		:role="position == 'left' || position == 'right' ? 'menu' : 'menubar'"
		:aria-label="$t(`navigation${capitalizeFirstLetter(position)}`)"
		:class="`ui ui--${position}`"
		v-if="navigationButtons.length > 0"
	>
		<ElementList
			:entries="navigationButtons"
			@onListElementAnimationEnd="onListElementAnimationEnd"
			ref="elementList"
		/>
		<!-- extra close button for screen reader added, because it could not activate other closebutton because of styling issues -->
		<ElementButton
			tabindex="0"
			v-if="closeButton"
			name="screen"
			@click="closeAndResetFocus"
			i18n
			content="close"
			style="visibility: none"
			ref="ariaCloseButton"
		/>
		<ElementButton
			tabindex="0"
			v-if="closeButton"
			name="close"
			@click="closeAndResetFocus"
			i18n
			aria-hidden="true"
		/>
		<slot />
	</nav>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import '../../tools/dataUrlLinkTap';
import { mutationTypes } from '../../types';
import UiBase from './base.vue';
import { uiComponents, uiStates } from '../../tools/viewState';
import { resolveStateURI } from '../../tools/storeURIHandler';
import { capitalizeFirstLetter, generateUUID } from '../../tools/helpers';
import { i18n } from '../../tools/i18n';

const storeRegExp = /^store:\/\//;

export default {
	extends: UiBase,
	props: {
		position: String,
		hideOnScroll: Boolean,
		closeButton: Boolean,
	},

	data: function () {
		return {
			name: `ui--${this.position}`,
			// Used to store the store values by watcher to achieve updateing navigation list
			buttonStoreMap: {},
			buttonStoreAnimationMap: {},
			// stores the store unwatch functions which are called if buttons change
			buttonStoreUnwatchers: [],
			isMinimized: false,
			lastFocusedElement: null,
		};
	},

	computed: {
		...mapState({
			buttons(state) {
				return state.navigation[this.position];
			},
			scrollLegacyDown(state) {
				return state.scroll.legacyDown;
			},
		}),
		...mapState(['navigation']),
		...mapGetters(['currentLanguage']),
		/**
		 *  creates Navigation Buttons from State navigation.position
		 *  if contentID is set title will be calucalated from assetDictionary
		 */
		navigationButtons() {
			return this.navigation[this.position].map((entry, index) => {
				let title = entry.contentID ? i18n.t(entry.contentID) : entry.content;

				if (typeof entry.content === 'object') title = entry.content;
				else if (storeRegExp.test(entry.content)) title = this.buttonStoreMap[entry.content];

				const data =
					entry.data &&
					Object.entries(entry.data).reduce((acc, [key, val]) => {
						acc[key] = typeof this.buttonStoreMap[val] !== 'undefined' ? this.buttonStoreMap[val] : val;
						return acc;
					}, {});
				if (/^(left|right)$/.test(this.position)) {
					// close left and right menu
					entry.click = () => {
						this[mutationTypes.viewStateSetUiState]({
							id: uiComponents[`navigation${capitalizeFirstLetter(this.position)}`],
							uiState: uiStates.hidden,
						});
					};
				}
				return {
					...entry,
					title,
					key: generateUUID(),
					data,
					dataUpdated: this.buttonStoreAnimationMap[index],
				};
			});
		},
	},

	methods: {
		...mapMutations([mutationTypes.viewStateSetUiState]),
		onListElementAnimationEnd(index) {
			this.buttonStoreAnimationMap[index] = false;
			this.buttonStoreAnimationMap = { ...this.buttonStoreAnimationMap };
		},
		capitalizeFirstLetter,
	},
	watch: {
		buttons(newButtons) {
			this.buttonStoreUnwatchers.forEach((unwatch) => {
				unwatch();
			});
			this.buttonStoreUnwatchers = [];
			this.buttonStoreMap = newButtons.reduce((acc, entry, index) => {
				if (storeRegExp.test(entry.content)) {
					// create store value watcher for button entry
					acc[entry.content] = resolveStateURI(entry.content, this.$store, {
						resolveLanguageContent: false,
					});
					this.buttonStoreUnwatchers.push(
						this.$store.watch(
							() => resolveStateURI(entry.content, this.$store, { resolveLanguageContent: false }),
							(n) => {
								this.buttonStoreMap[entry.content] = n;
								this.buttonStoreAnimationMap[index] = true;
							}
						)
					);
				}
				if (entry.data && Object.keys(entry.data).length >= 0) {
					Object.entries(entry.data).forEach(([, val]) => {
						if (storeRegExp.test(val)) {
							// create store value watcher for button entry
							acc[val] = resolveStateURI(val, this.$store, { resolveLanguageContent: false });
							this.buttonStoreUnwatchers.push(
								this.$store.watch(
									() => resolveStateURI(val, this.$store, { resolveLanguageContent: false }),
									(n) => {
										this.buttonStoreMap[val] = n;
										this.buttonStoreAnimationMap[index] = true;
									}
								)
							);
						}
					});
				}

				return acc;
			}, {});
			// The top and bottom naviation menus are not programmatically shown or hidden
			// but they are always shown when they have content, and always hidden when they have no
			// content.
			// FIXME this code should be in the code that reads the navigation.
			if (['top', 'bottom', 'tour', 'quiz'].includes(this.position)) {
				if (newButtons.length > 0) {
					this.maximise();
				} else {
					this.close();
				}
			}
		},
		// legacy menubar hiding
		scrollLegacyDown(scrolledDown) {
			if (this.hideOnScroll) {
				if (scrolledDown) {
					this.minimise();
				} else {
					this.maximise();
				}
			}
		},
	},
	mounted() {
		// set focus on first element if it is used as an menue which is opend by the user
		// this is true it has an close button
		if (this.closeButton) {
			if (this.$refs.elementList && this.$refs.elementList.$el.childElementCount > 0) {
				this.$refs.elementList.$el.childNodes[0].focus();
			}
		}
	},

	destroy() {
		if (this.interactable) {
			this.interactable.unset();
		}
	},
};
</script>
